import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { useMutation } from '@tanstack/react-query';
import { forgotPassword } from '../services/auth';
import { AuthScreen } from '../components/AuthScreen';
import { FormGroup, Label, Input, ErrorAlert, AlertBanner, Button } from '../components/styled';

const FullButton = styled(Button)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const FooterRow = styled.div`
  margin-top: ${({ theme }) => theme.spacing(6)};
  font-size: ${({ theme }) => theme.font.size.sm};
`;

const BackLink = styled(Link)`
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.font.weight.medium};

  &:hover {
    text-decoration: underline;
  }
`;

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => forgotPassword(email),
    onError: (err: any) => setError(err.response?.data?.error || 'Something went wrong. Please try again.'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  return (
    <AuthScreen
      title="Reset your password"
      subtitle="Enter your account email and we'll send you a link to set a new password."
    >
      {mutation.isSuccess ? (
        <>
          <AlertBanner variant="success">
            If an account exists for that email, a reset link is on its way. Check your inbox.
          </AlertBanner>
          <FooterRow>
            <BackLink to="/login">Back to sign in</BackLink>
          </FooterRow>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          {error && <ErrorAlert>{error}</ErrorAlert>}
          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </FormGroup>
          <FullButton type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Sending...' : 'Send reset link'}
          </FullButton>
          <FooterRow>
            <BackLink to="/login">Back to sign in</BackLink>
          </FooterRow>
        </form>
      )}
    </AuthScreen>
  );
}

export default ForgotPassword;
